import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Navbar from '../components/Navbar';
import {
  AI_IMAGE_STYLE_CATEGORIES,
  AI_IMAGE_STYLE_TOTAL_COUNT,
  AI_VOICE_PERSONAS,
  requestAIImage,
  requestAIVoiceover,
  getLipSyncEnvelope,
} from '../services/aiStudio';

// ---------- helpers ----------
function useAnimationClock() {
  const [elapsed, setElapsed] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const playingRef = useRef(false);

  const stop = useCallback(() => {
    playingRef.current = false;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  const start = useCallback((durationSec, onDone) => {
    playingRef.current = true;
    startRef.current = performance.now();
    const tick = () => {
      if (!playingRef.current) return;
      const t = (performance.now() - startRef.current) / 1000;
      if (t >= durationSec) {
        setElapsed(durationSec);
        stop();
        onDone && onDone();
        return;
      }
      setElapsed(t);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [stop]);

  return { elapsed, start, stop, playingRef };
}

// Responsive helper — mirrors a CSS media query in JS so inline-style layout
// can react to viewport width (component uses inline styles, not classes).
function useIsMobile(breakpoint = 760) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, [breakpoint]);
  return isMobile;
}

const CAM_TYPES = [
  { value: 'bounce-in', label: 'Bouncy push-in' },
  { value: 'pop-out', label: 'Pop out (reveal)' },
  { value: 'wiggle-pan', label: 'Wiggle pan L→R' },
  { value: 'swing', label: 'Pendulum swing' },
  { value: 'hop', label: 'Hop / skip (bouncing)' },
  { value: 'still', label: 'Static (no motion)' },
];

const ASPECTS = {
  classic: { label: 'Classic (18:25)', w: 720, h: 1000 },
  story: { label: 'Reel / Story (9:16)', w: 720, h: 1280 },
  post: { label: 'Instagram post (4:5)', w: 900, h: 1125 },
  square: { label: 'Square (1:1)', w: 900, h: 900 },
  landscape: { label: 'YouTube (16:9)', w: 1280, h: 720 },
};

const BG_PRESETS = ['#fff8e8', '#0f0f1a', '#ffe8f0', '#e8f4ff', '#101014'];

const FILTERS = {
  none: { label: 'None', css: 'none' },
  warm: { label: 'Warm', css: 'sepia(0.25) saturate(1.35) brightness(1.05)' },
  cool: { label: 'Cool', css: 'saturate(1.15) hue-rotate(-8deg) contrast(1.05)' },
  bw: { label: 'Black & White', css: 'grayscale(1) contrast(1.1)' },
  sepia: { label: 'Sepia', css: 'sepia(0.75) contrast(1.05)' },
  vivid: { label: 'Vivid pop', css: 'saturate(1.65) contrast(1.18) brightness(1.03)' },
  vintage: { label: 'Vintage', css: 'sepia(0.35) saturate(0.85) contrast(0.92) brightness(1.05)' },
};

const TRANSITIONS = {
  none: 'Cut (no transition)',
  fade: 'Cross-fade',
  slide: 'Slide L→R',
  zoom: 'Zoom blend',
};

const TEXT_ANIMS = { fade: 'Fade in', pop: 'Pop in', slideup: 'Slide up' };
const TEXT_POSITIONS = {
  top: { label: 'Top', x: 0.5, y: 0.14 },
  center: { label: 'Center', x: 0.5, y: 0.5 },
  bottom: { label: 'Bottom', x: 0.5, y: 0.86 },
};

// ---------- CapCut-style timeline + bottom toolbar config ----------
const PX_PER_SEC = 22; // horizontal scale of the timeline (px per second of clip duration)
const MIN_CLIP_DUR = 1;
const MAX_CLIP_DUR = 30;
const CLIP_GAP = 6;

const PANEL_DEFS = [
  { key: 'canvas', icon: '📐', label: 'Canvas' },
  { key: 'edit', icon: '🖼️', label: 'Edit' },
  { key: 'ai', icon: '🪄', label: 'AI Studio' },
  { key: 'camera', icon: '🎥', label: 'Camera' },
  { key: 'filters', icon: '🎨', label: 'Filters' },
  { key: 'transition', icon: '🔀', label: 'Transition' },
  { key: 'text', icon: '🔤', label: 'Text' },
  { key: 'effects', icon: '✨', label: 'Effects' },
  { key: 'voice', icon: '🎙️', label: 'Voice' },
  { key: 'music', icon: '🎵', label: 'Audio' },
  { key: 'project', icon: '📁', label: 'Project' },
];

function camState(type, t, intensity) {
  const amt = intensity / 100;
  const bounce = Math.abs(Math.sin(t * Math.PI * 4)) * amt * 0.12;
  switch (type) {
    case 'bounce-in':
      return { scale: 1 + amt * 0.5 * t + bounce, x: 0, y: -bounce * 60, rot: Math.sin(t * 20) * 0.01 * amt };
    case 'pop-out':
      return { scale: 1 + amt * 0.5 * (1 - t), x: 0, y: 0, rot: 0 };
    case 'wiggle-pan':
      return { scale: 1 + amt * 0.2, x: (t - 0.5) * amt * 260, y: Math.sin(t * Math.PI * 6) * amt * 12, rot: Math.sin(t * Math.PI * 6) * 0.02 * amt };
    case 'swing':
      return { scale: 1 + amt * 0.15, x: Math.sin(t * Math.PI * 2) * amt * 90, y: 0, rot: Math.sin(t * Math.PI * 2) * 0.06 * amt };
    case 'hop':
      return { scale: 1 + amt * 0.2, x: 0, y: -Math.abs(Math.sin(t * Math.PI * 5)) * amt * 70, rot: 0 };
    case 'still':
      return { scale: 1, x: 0, y: 0, rot: 0 };
    default:
      return { scale: 1, x: 0, y: 0, rot: 0 };
  }
}

function applyToonVFX(ctx, w, h, elapsed, t, vfx, confettiRef) {
  if (vfx.outline) {
    ctx.save();
    ctx.globalCompositeOperation = 'multiply';
    ctx.strokeStyle = 'rgba(20,10,40,0.35)';
    ctx.lineWidth = 6;
    ctx.strokeRect(3, 3, w - 6, h - 6);
    ctx.restore();
  }
  if (vfx.colorPop) {
    ctx.save();
    ctx.globalCompositeOperation = 'overlay';
    ctx.fillStyle = 'rgba(255,214,102,0.10)';
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
  }
  if (vfx.halftone) {
    ctx.save();
    ctx.globalAlpha = 0.06;
    ctx.fillStyle = '#000';
    const spacing = 10;
    for (let y = 0; y < h; y += spacing) {
      for (let x = (y / spacing) % 2 === 0 ? 0 : spacing / 2; x < w; x += spacing) {
        ctx.beginPath();
        ctx.arc(x, y, 1.4, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.restore();
  }
  if (vfx.speedLines && Math.floor(elapsed * 2) % 2 === 0) {
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.10)';
    ctx.lineWidth = 2;
    for (let i = 0; i < 14; i++) {
      const yy = (i / 14) * h + (elapsed * 200 % h);
      ctx.beginPath();
      ctx.moveTo(0, yy % h);
      ctx.lineTo(w, (yy % h) - 40);
      ctx.stroke();
    }
    ctx.restore();
  }
  if (vfx.sparkle) {
    for (let i = 0; i < 8; i++) {
      const px = (Math.sin(elapsed * 1.7 + i * 12.9) * 0.5 + 0.5) * w;
      const py = (Math.cos(elapsed * 1.3 + i * 7.3) * 0.5 + 0.5) * h;
      const r = 2 + Math.sin(elapsed * 6 + i) * 2;
      ctx.save();
      ctx.fillStyle = `rgba(255,255,255,${0.4 + Math.sin(elapsed * 5 + i) * 0.3})`;
      ctx.beginPath();
      ctx.moveTo(px, py - r * 2); ctx.lineTo(px + r * 0.5, py - r * 0.5);
      ctx.lineTo(px + r * 2, py); ctx.lineTo(px + r * 0.5, py + r * 0.5);
      ctx.lineTo(px, py + r * 2); ctx.lineTo(px - r * 0.5, py + r * 0.5);
      ctx.lineTo(px - r * 2, py); ctx.lineTo(px - r * 0.5, py - r * 0.5);
      ctx.closePath(); ctx.fill();
      ctx.restore();
    }
  }
  if (vfx.confetti) {
    if (!confettiRef.current.length && t > 0.15 && t < 0.2) {
      const colors = ['#ff6b93', '#ffd666', '#6c9bff', '#7be0a0', '#c8b8ff'];
      confettiRef.current = Array.from({ length: 60 }, () => ({
        x: w / 2 + (Math.random() - 0.5) * 40,
        y: h * 0.3,
        vx: (Math.random() - 0.5) * 260,
        vy: -Math.random() * 300 - 60,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 4 + Math.random() * 5,
        rot: Math.random() * Math.PI,
      }));
    }
    if (confettiRef.current.length) {
      const dt = 1 / 60;
      confettiRef.current.forEach(p => {
        p.vy += 500 * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.rot += 4 * dt;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
        ctx.restore();
      });
    }
  }
  if (vfx.popFlash && Math.abs(t - 0.5) < 0.02) {
    ctx.save();
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
  }
}

function drawTextOverlays(ctx, w, h, texts, tAnim) {
  if (!texts || !texts.length) return;
  const inDur = 0.15;
  texts.forEach(txt => {
    if (!txt.text) return;
    const delay = txt.delay || 0;
    const p = Math.max(0, Math.min(1, (tAnim - delay) / inDur));
    if (p <= 0) return;
    let alpha = 1, scale = 1, dy = 0;
    if (txt.anim === 'fade') { alpha = p; }
    else if (txt.anim === 'pop') { alpha = p; scale = 0.6 + 0.4 * p + (p < 1 ? Math.sin(p * Math.PI) * 0.08 : 0); }
    else if (txt.anim === 'slideup') { alpha = p; dy = (1 - p) * 40; }
    ctx.save();
    ctx.globalAlpha = alpha;
    const px = (txt.x == null ? 0.5 : txt.x) * w;
    const py = (txt.y == null ? 0.86 : txt.y) * h + dy;
    const size = txt.size || 42;
    ctx.translate(px, py);
    ctx.scale(scale, scale);
    ctx.font = `800 ${size}px 'Poppins', 'Segoe UI', sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round';
    ctx.lineWidth = size * 0.16;
    ctx.strokeStyle = 'rgba(0,0,0,0.55)';
    ctx.strokeText(txt.text, 0, 0);
    ctx.fillStyle = txt.color || '#ffffff';
    ctx.fillText(txt.text, 0, 0);
    ctx.restore();
  });
}

function renderClipFrame(ctx, w, h, clip, tAnim, elapsedForVfx, confettiRef) {
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = clip.bgColor;
  ctx.fillRect(0, 0, w, h);

  const cam = camState(clip.camType, tAnim, clip.camIntensity);
  ctx.filter = (FILTERS[clip.filter] && FILTERS[clip.filter].css) || 'none';

  clip.layers.forEach(layer => {
    if (!layer.img || layer.visible === false) return;
    ctx.save();
    ctx.globalAlpha = layer.opacity == null ? 1 : layer.opacity;
    const parallaxMul = 0.3 + layer.depth * 1.4;
    const localScale = cam.scale * (1 + layer.depth * 0.12);
    ctx.translate(w / 2 + cam.x * parallaxMul, h / 2 + cam.y * parallaxMul);
    ctx.rotate(cam.rot);
    ctx.scale(localScale, localScale);
    const img = layer.img;
    const imgRatio = img.width / img.height;
    const frameRatio = w / h;
    let dw, dh;
    if (imgRatio > frameRatio) { dh = h; dw = h * imgRatio; } else { dw = w; dh = w / imgRatio; }
    ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
    ctx.restore();
  });

  ctx.filter = 'none';
  applyToonVFX(ctx, w, h, elapsedForVfx, tAnim, clip.vfx, confettiRef);
  drawTextOverlays(ctx, w, h, clip.texts, tAnim);
  if (clip.lipSync && clip.lipSync.enabled) drawLipSyncOverlay(ctx, w, h, elapsedForVfx, clip.lipSync);
}

// Stand-in for real viseme-driven lip-sync: a mouth "flap" indicator driven
// by a synthetic audio envelope (see services/aiStudio.js getLipSyncEnvelope),
// plus a badge so it's obvious in preview/export that sync is active.
function drawLipSyncOverlay(ctx, w, h, elapsed, lipSync) {
  const amp = getLipSyncEnvelope(elapsed, lipSync.intensity);
  ctx.save();
  ctx.globalAlpha = 0.9;
  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  const bw = 128, bh = 26;
  ctx.beginPath();
  ctx.roundRect ? ctx.roundRect(10, h - bh - 10, bw, bh, 13) : ctx.rect(10, h - bh - 10, bw, bh);
  ctx.fill();
  ctx.fillStyle = '#ffd666';
  ctx.font = '700 11px "Poppins", sans-serif';
  ctx.textBaseline = 'middle';
  ctx.fillText('👄 Lip-sync ON', 22, h - bh / 2 - 10 + 13);
  ctx.restore();

  // Small mouth-flap dot near bottom-center, pulsing with the envelope —
  // a visual placeholder until real phoneme-driven mouth shapes exist.
  ctx.save();
  ctx.translate(w / 2, h * 0.86);
  ctx.fillStyle = 'rgba(255,107,147,0.85)';
  ctx.beginPath();
  ctx.ellipse(0, 0, 14, 6 + amp * 10, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

const DRAFT_KEY = 'animora-draft-v2';

let uidCounter = 1;
const uid = () => uidCounter++;

function makeClip(name) {
  return {
    id: uid(),
    name: name || 'Scene',
    layers: [
      { id: uid(), img: null, name: 'background', depth: 0, opacity: 1, visible: true },
      { id: uid(), img: null, name: 'character', depth: 0.7, opacity: 1, visible: true },
    ],
    camType: 'bounce-in',
    camIntensity: 32,
    duration: 6,
    speed: 1,
    bgColor: '#fff8e8',
    filter: 'none',
    vfx: { outline: true, colorPop: true, halftone: false, speedLines: false, sparkle: true, confetti: true, popFlash: false },
    texts: [],
    transition: 'none',
    lipSync: { enabled: false, intensity: 55, source: 'voiceover' },
  };
}

export default function Animora() {
  const isMobile = useIsMobile();
  const canvasRef = useRef(null);
  const currCanvasRef = useRef(null);
  const prevCanvasRef = useRef(null);
  if (!currCanvasRef.current && typeof document !== 'undefined') currCanvasRef.current = document.createElement('canvas');
  if (!prevCanvasRef.current && typeof document !== 'undefined') prevCanvasRef.current = document.createElement('canvas');

  const [clips, setClips] = useState(() => [makeClip('Scene 1')]);
  const [selectedClipId, setSelectedClipId] = useState(() => clips[0].id);
  const lastDeletedRef = useRef(null);
  const [hasLastDeleted, setHasLastDeleted] = useState(false);

  const [aspectKey, setAspectKey] = useState('classic');
  const [loop, setLoop] = useState(false);

  const [script, setScript] = useState('Ek chhota sa raasta, ek badi si kahani — chalo dekhte hain aage kya hota hai!');
  const [voices, setVoices] = useState([]);
  const [voiceIdx, setVoiceIdx] = useState(0);
  const [voiceMood, setVoiceMood] = useState('cheerful');

  // ---- AI Studio state (text-to-image, AI voiceover, lip-sync) ----
  const [aiSubTab, setAiSubTab] = useState('image');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiCategory, setAiCategory] = useState(AI_IMAGE_STYLE_CATEGORIES[0].category);
  const [aiStyle, setAiStyle] = useState(AI_IMAGE_STYLE_CATEGORIES[0].styles[0]);
  const [aiGeneratingImage, setAiGeneratingImage] = useState(false);
  const [aiImageResults, setAiImageResults] = useState([]);
  const [aiImageTarget, setAiImageTarget] = useState('character');
  const [aiPersonaId, setAiPersonaId] = useState(AI_VOICE_PERSONAS[0].id);
  const [aiGeneratingVoice, setAiGeneratingVoice] = useState(false);
  const [aiVoiceTakes, setAiVoiceTakes] = useState([]);
  const [selectedTakeId, setSelectedTakeId] = useState(null);

  const [ambience, setAmbience] = useState({ boing: true, chime: false, giggle: false });
  const [uploadedSfx, setUploadedSfx] = useState([]);
  const [music, setMusic] = useState(null);
  const [musicVolume, setMusicVolume] = useState(0.6);
  const [musicOn, setMusicOn] = useState(true);

  const [recording, setRecording] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [manualTime, setManualTime] = useState(0);

  // ---- CapCut-style UI state: which bottom-sheet panel is open, and
  // undo/redo history for structural timeline edits (add/remove/duplicate/
  // reorder/trim scenes) ----
  const [activePanel, setActivePanel] = useState(null);
  const togglePanel = (key) => setActivePanel(p => (p === key ? null : key));
  const historyRef = useRef([]);
  const redoRef = useRef([]);
  const trackRef = useRef(null);
  const [historyTick, setHistoryTick] = useState(0); // bumped to re-render undo/redo button state

  const { elapsed, start, stop, playingRef } = useAnimationClock();
  const confettiMapRef = useRef({});
  const lastClipIndexRef = useRef(-1);
  const audioCtxRef = useRef(null);
  const mixerGainRef = useRef(null);
  const ambNodesRef = useRef({});
  const musicElRef = useRef(null);
  const musicSourceRef = useRef(null);
  const musicGainRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const canvasSize = ASPECTS[aspectKey];
  const selectedClip = useMemo(
    () => clips.find(c => c.id === selectedClipId) || clips[0],
    [clips, selectedClipId]
  );
  const selectedIdx = clips.findIndex(c => c.id === selectedClip.id);
  const totalDuration = useMemo(() => clips.reduce((a, c) => a + c.duration, 0), [clips]);
  const hasAnyImage = clips.some(c => c.layers.some(l => l.img));

  const getConfettiRef = (clipId) => {
    if (!confettiMapRef.current[clipId]) confettiMapRef.current[clipId] = { current: [] };
    return confettiMapRef.current[clipId];
  };

  // Resize offscreen buffers whenever the export aspect ratio changes.
  useEffect(() => {
    [currCanvasRef, prevCanvasRef].forEach(ref => {
      if (ref.current) { ref.current.width = canvasSize.w; ref.current.height = canvasSize.h; }
    });
    lastClipIndexRef.current = -1;
  }, [canvasSize.w, canvasSize.h]);

  const draw = useCallback((globalElapsed) => {
    const canvas = canvasRef.current;
    const curr = currCanvasRef.current;
    const prevBuf = prevCanvasRef.current;
    if (!canvas || !curr || !prevBuf) return;
    const w = canvas.width, h = canvas.height;
    const ctx = canvas.getContext('2d');

    let cumulative = 0, clipIndex = 0, localElapsed = 0;
    for (let i = 0; i < clips.length; i++) {
      if (globalElapsed < cumulative + clips[i].duration || i === clips.length - 1) {
        clipIndex = i;
        localElapsed = Math.max(0, globalElapsed - cumulative);
        break;
      }
      cumulative += clips[i].duration;
    }
    const clip = clips[clipIndex];
    const tAnim = Math.min(1, (localElapsed / clip.duration) * clip.speed);

    // Snapshot the previous frame right when we cross into a new clip, so a
    // transition has something to blend from.
    if (clipIndex !== lastClipIndexRef.current) {
      const prevCtx = prevBuf.getContext('2d');
      prevCtx.clearRect(0, 0, w, h);
      prevCtx.drawImage(canvas, 0, 0);
      lastClipIndexRef.current = clipIndex;
    }

    const currCtx = curr.getContext('2d');
    renderClipFrame(currCtx, w, h, clip, tAnim, globalElapsed, getConfettiRef(clip.id));

    const transitionDur = clip.transition !== 'none' && clipIndex > 0 ? Math.min(1.2, clip.duration * 0.4) : 0;
    const p = transitionDur > 0 ? Math.min(1, localElapsed / transitionDur) : 1;

    ctx.clearRect(0, 0, w, h);
    if (p >= 1) {
      ctx.drawImage(curr, 0, 0);
    } else {
      switch (clip.transition) {
        case 'fade':
          ctx.globalAlpha = 1; ctx.drawImage(prevBuf, 0, 0);
          ctx.globalAlpha = p; ctx.drawImage(curr, 0, 0);
          ctx.globalAlpha = 1;
          break;
        case 'slide':
          ctx.drawImage(prevBuf, -p * w, 0);
          ctx.drawImage(curr, (1 - p) * w, 0);
          break;
        case 'zoom':
          ctx.save();
          ctx.globalAlpha = 1 - p;
          ctx.translate(w / 2, h / 2); ctx.scale(1 + p * 0.4, 1 + p * 0.4);
          ctx.drawImage(prevBuf, -w / 2, -h / 2);
          ctx.restore();
          ctx.save();
          ctx.globalAlpha = p;
          const sc = 1.3 - 0.3 * p;
          ctx.translate(w / 2, h / 2); ctx.scale(sc, sc);
          ctx.drawImage(curr, -w / 2, -h / 2);
          ctx.restore();
          break;
        default:
          ctx.drawImage(curr, 0, 0);
      }
    }
  }, [clips]);

  // Single redraw pipeline: while playing, follow the animation clock;
  // otherwise follow the manual scrub position. Also redraws on aspect
  // ratio change since resizing the canvas element clears its pixels.
  useEffect(() => {
    const t = playingRef.current ? elapsed : manualTime;
    draw(t);
  }, [elapsed, manualTime, draw, aspectKey, playingRef]);

  useEffect(() => { setManualTime(m => Math.min(m, totalDuration)); }, [totalDuration]);

  // ---------- clip (scene) management ----------
  const patchClip = (id, patch) => {
    setClips(prev => prev.map(c => (c.id === id ? (typeof patch === 'function' ? patch(c) : { ...c, ...patch }) : c)));
  };

  const addClip = () => {
    pushHistorySnapshot();
    const nc = makeClip(`Scene ${clips.length + 1}`);
    setClips(prev => [...prev, nc]);
    setSelectedClipId(nc.id);
  };
  const removeClip = (id) => {
    if (clips.length <= 1) { alert('Kam se kam ek scene rehna chahiye.'); return; }
    pushHistorySnapshot();
    const idx = clips.findIndex(c => c.id === id);
    lastDeletedRef.current = { clip: clips[idx], index: idx };
    setHasLastDeleted(true);
    setClips(prev => {
      const next = prev.filter(c => c.id !== id);
      if (id === selectedClipId) setSelectedClipId(next[Math.max(0, idx - 1)].id);
      return next;
    });
  };
  const undoDeleteClip = () => {
    if (!lastDeletedRef.current) return;
    const { clip, index } = lastDeletedRef.current;
    setClips(prev => {
      const next = [...prev];
      next.splice(Math.min(index, next.length), 0, clip);
      return next;
    });
    setSelectedClipId(clip.id);
    lastDeletedRef.current = null;
    setHasLastDeleted(false);
  };
  const duplicateClip = (id) => {
    pushHistorySnapshot();
    setClips(prev => {
      const idx = prev.findIndex(c => c.id === id);
      if (idx === -1) return prev;
      const src = prev[idx];
      const copy = {
        ...src,
        id: uid(),
        name: src.name + ' copy',
        layers: src.layers.map(l => ({ ...l, id: uid() })),
        texts: src.texts.map(t => ({ ...t, id: uid() })),
        vfx: { ...src.vfx },
        lipSync: { ...(src.lipSync || { enabled: false, intensity: 55, source: 'voiceover' }) },
      };
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
  };
  const moveClip = (id, dir) => {
    pushHistorySnapshot();
    setClips(prev => {
      const idx = prev.findIndex(c => c.id === id);
      const swapIdx = idx + dir;
      if (idx < 0 || swapIdx < 0 || swapIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
      return next;
    });
  };
  const renameClip = (id, name) => patchClip(id, { name });

  // ---------- undo / redo (structural scene edits: add / remove / duplicate
  // / reorder / trim) ----------
  const pushHistorySnapshot = () => {
    historyRef.current.push(clips);
    if (historyRef.current.length > 40) historyRef.current.shift();
    redoRef.current = [];
    setHistoryTick(t => t + 1);
  };
  const undo = () => {
    if (!historyRef.current.length) return;
    setClips(curr => {
      const prev = historyRef.current.pop();
      redoRef.current.push(curr);
      return prev;
    });
    setHistoryTick(t => t + 1);
  };
  const redo = () => {
    if (!redoRef.current.length) return;
    setClips(curr => {
      const next = redoRef.current.pop();
      historyRef.current.push(curr);
      return next;
    });
    setHistoryTick(t => t + 1);
  };

  // ---------- timeline drag: reorder clips by dragging a clip block ----------
  const beginReorderDrag = (e, clipId) => {
    if (clips.length < 2) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const startX = e.clientX;
    const startClips = clips;
    const widths = {};
    startClips.forEach(c => { widths[c.id] = Math.max(70, c.duration * PX_PER_SEC); });
    const orderIds = startClips.map(c => c.id);
    let dragging = false;
    let currentIndex = orderIds.indexOf(clipId);

    const computeLefts = (ids) => {
      const lefts = {};
      let acc = 0;
      ids.forEach(id => { lefts[id] = acc; acc += widths[id] + CLIP_GAP; });
      return lefts;
    };

    const onMove = (ev) => {
      const deltaX = ev.clientX - startX;
      if (!dragging) {
        if (Math.abs(deltaX) < 6) return;
        dragging = true;
        pushHistorySnapshot();
      }
      const track = trackRef.current;
      if (!track) return;
      ev.preventDefault();
      const rect = track.getBoundingClientRect();
      const pointerX = ev.clientX - rect.left + track.scrollLeft;
      const lefts = computeLefts(orderIds);
      let targetIndex = orderIds.length - 1;
      for (let i = 0; i < orderIds.length; i++) {
        const id = orderIds[i];
        const center = lefts[id] + widths[id] / 2;
        if (pointerX < center) { targetIndex = i; break; }
      }
      if (targetIndex !== currentIndex) {
        orderIds.splice(currentIndex, 1);
        orderIds.splice(targetIndex, 0, clipId);
        currentIndex = targetIndex;
        setClips(prev => {
          const map = {};
          prev.forEach(c => { map[c.id] = c; });
          return orderIds.map(id => map[id]);
        });
      }
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // ---------- timeline drag: trim a clip's duration from its right edge ----------
  const trimClipLive = (id, duration) => {
    setClips(prev => prev.map(c => (c.id === id ? { ...c, duration } : c)));
  };
  const beginTrimDrag = (e, clipId, currentDuration) => {
    e.stopPropagation();
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    pushHistorySnapshot();
    const startX = e.clientX;
    const onMove = (ev) => {
      ev.preventDefault();
      const deltaX = ev.clientX - startX;
      const deltaSec = deltaX / PX_PER_SEC;
      const raw = currentDuration + deltaSec;
      const newDur = Math.max(MIN_CLIP_DUR, Math.min(MAX_CLIP_DUR, Math.round(raw * 10) / 10));
      trimClipLive(clipId, newDur);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // ---------- layer management (scoped to a clip) ----------
  const addLayer = (clipId) => {
    patchClip(clipId, c => ({ ...c, layers: [...c.layers, { id: uid(), img: null, name: 'layer ' + c.layers.length, depth: 0.5, opacity: 1, visible: true }] }));
  };
  const removeLayer = (clipId, layerId) => patchClip(clipId, c => ({ ...c, layers: c.layers.filter(l => l.id !== layerId) }));
  const setLayerDepth = (clipId, layerId, depth) => patchClip(clipId, c => ({ ...c, layers: c.layers.map(l => l.id === layerId ? { ...l, depth } : l) }));
  const setLayerOpacity = (clipId, layerId, opacity) => patchClip(clipId, c => ({ ...c, layers: c.layers.map(l => l.id === layerId ? { ...l, opacity } : l) }));
  const toggleLayerVisible = (clipId, layerId) => patchClip(clipId, c => ({ ...c, layers: c.layers.map(l => l.id === layerId ? { ...l, visible: l.visible === false } : l) }));
  const setLayerImage = (clipId, layerId, file) => {
    if (!file) return;
    const img = new Image();
    img.onload = () => patchClip(clipId, c => ({ ...c, layers: c.layers.map(l => l.id === layerId ? { ...l, img } : l) }));
    img.src = URL.createObjectURL(file);
  };
  const addLayerFromDataUrl = (clipId, dataUrl, name) => {
    const img = new Image();
    img.onload = () => {
      patchClip(clipId, c => ({
        ...c,
        layers: [...c.layers, { id: uid(), img, name: name || 'AI layer', depth: 0.6, opacity: 1, visible: true }],
      }));
    };
    img.src = dataUrl;
  };
  const setLayerImageFromDataUrl = (clipId, layerId, dataUrl) => {
    const img = new Image();
    img.onload = () => patchClip(clipId, c => ({ ...c, layers: c.layers.map(l => l.id === layerId ? { ...l, img } : l) }));
    img.src = dataUrl;
  };
  const moveLayer = (clipId, layerId, dir) => {
    patchClip(clipId, c => {
      const idx = c.layers.findIndex(l => l.id === layerId);
      const swapIdx = idx + dir;
      if (idx < 0 || swapIdx < 0 || swapIdx >= c.layers.length) return c;
      const layers = [...c.layers];
      [layers[idx], layers[swapIdx]] = [layers[swapIdx], layers[idx]];
      return { ...c, layers };
    });
  };
  const duplicateLayer = (clipId, layerId) => {
    patchClip(clipId, c => {
      const idx = c.layers.findIndex(l => l.id === layerId);
      if (idx === -1) return c;
      const copy = { ...c.layers[idx], id: uid(), name: c.layers[idx].name + ' copy' };
      const layers = [...c.layers];
      layers.splice(idx + 1, 0, copy);
      return { ...c, layers };
    });
  };

  const toggleVfx = (clipId, key) => patchClip(clipId, c => ({ ...c, vfx: { ...c.vfx, [key]: !c.vfx[key] } }));

  // ---------- text overlay management ----------
  const addText = (clipId) => {
    patchClip(clipId, c => ({
      ...c,
      texts: [...c.texts, { id: uid(), text: 'Naya text', x: 0.5, y: 0.86, color: '#ffffff', size: 42, anim: 'pop', delay: 0.1 }],
    }));
  };
  const updateText = (clipId, textId, patch) => patchClip(clipId, c => ({ ...c, texts: c.texts.map(t => t.id === textId ? { ...t, ...patch } : t) }));
  const removeText = (clipId, textId) => patchClip(clipId, c => ({ ...c, texts: c.texts.filter(t => t.id !== textId) }));
  const setTextPosition = (clipId, textId, posKey) => {
    const pos = TEXT_POSITIONS[posKey];
    updateText(clipId, textId, { x: pos.x, y: pos.y });
  };

  // ---------- voice-over ----------
  useEffect(() => {
    const load = () => setVoices(window.speechSynthesis.getVoices());
    load();
    window.speechSynthesis.onvoiceschanged = load;
  }, []);

  // ---------- AI Studio: text-to-image ----------
  const generateAIImages = async () => {
    if (!aiPrompt.trim() || aiGeneratingImage) return;
    setAiGeneratingImage(true);
    try {
      const results = await requestAIImage({ prompt: aiPrompt, style: aiStyle });
      setAiImageResults(results);
    } finally {
      setAiGeneratingImage(false);
    }
  };
  const applyAIImage = (result) => {
    if (aiImageTarget === 'new') {
      addLayerFromDataUrl(selectedClip.id, result.url, `AI: ${result.style}`);
      return;
    }
    const layer = selectedClip.layers.find(l => l.name === aiImageTarget) || selectedClip.layers[0];
    if (layer) setLayerImageFromDataUrl(selectedClip.id, layer.id, result.url);
  };

  // ---------- AI Studio: AI voiceover ----------
  const generateAIVoiceTakes = async () => {
    if (!script.trim() || aiGeneratingVoice) return;
    setAiGeneratingVoice(true);
    try {
      const takes = await requestAIVoiceover({ text: script, personaId: aiPersonaId });
      setAiVoiceTakes(takes);
      setSelectedTakeId(null);
    } finally {
      setAiGeneratingVoice(false);
    }
  };
  const useVoiceTake = (take) => setSelectedTakeId(take.id);

  // ---------- AI Studio: lip-sync ----------
  const toggleLipSync = (clipId) => patchClip(clipId, c => ({ ...c, lipSync: { ...c.lipSync, enabled: !c.lipSync.enabled } }));
  const setLipSyncIntensity = (clipId, intensity) => patchClip(clipId, c => ({ ...c, lipSync: { ...c.lipSync, intensity } }));
  const setLipSyncSource = (clipId, source) => patchClip(clipId, c => ({ ...c, lipSync: { ...c.lipSync, source } }));

  const speak = () => {
    if (!script.trim()) return;
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(script);
    if (voices[voiceIdx]) utter.voice = voices[voiceIdx];
    if (voiceMood === 'cheerful') { utter.pitch = 1.3; utter.rate = 1.08; }
    else if (voiceMood === 'silly') { utter.pitch = 1.6; utter.rate = 1.25; }
    else { utter.pitch = 1; utter.rate = 1; }
    window.speechSynthesis.speak(utter);
  };

  // ---------- audio graph (ambience + music share one mixer so both the
  // speakers AND the recording destination hear the same signal) ----------
  const ensureAudio = () => {
    if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    if (!mixerGainRef.current) {
      mixerGainRef.current = audioCtxRef.current.createGain();
      mixerGainRef.current.connect(audioCtxRef.current.destination);
    }
    return audioCtxRef.current;
  };

  const startAmb = (key) => {
    const ac = ensureAudio();
    const mixer = mixerGainRef.current;
    if (ambNodesRef.current[key]) return;
    if (key === 'chime') {
      const notes = [523, 659, 784];
      let i = 0;
      const interval = setInterval(() => {
        const osc = ac.createOscillator(); osc.type = 'sine'; osc.frequency.value = notes[i % notes.length];
        const g = ac.createGain(); g.gain.setValueAtTime(0.06, ac.currentTime); g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.5);
        osc.connect(g); g.connect(mixer);
        osc.start(); osc.stop(ac.currentTime + 0.5);
        i++;
      }, 1400);
      ambNodesRef.current.chime = { stop: () => clearInterval(interval) };
    } else if (key === 'boing') {
      const interval = setInterval(() => {
        const osc = ac.createOscillator(); osc.type = 'sine';
        osc.frequency.setValueAtTime(180, ac.currentTime);
        osc.frequency.exponentialRampToValueAtTime(60, ac.currentTime + 0.25);
        const g = ac.createGain(); g.gain.setValueAtTime(0.08, ac.currentTime); g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.3);
        osc.connect(g); g.connect(mixer);
        osc.start(); osc.stop(ac.currentTime + 0.3);
      }, 2200);
      ambNodesRef.current.boing = { stop: () => clearInterval(interval) };
    } else if (key === 'giggle') {
      const interval = setInterval(() => {
        for (let i = 0; i < 3; i++) {
          const osc = ac.createOscillator(); osc.type = 'triangle';
          osc.frequency.value = 500 + i * 80;
          const g = ac.createGain();
          const startT = ac.currentTime + i * 0.1;
          g.gain.setValueAtTime(0.05, startT); g.gain.exponentialRampToValueAtTime(0.001, startT + 0.15);
          osc.connect(g); g.connect(mixer);
          osc.start(startT); osc.stop(startT + 0.15);
        }
      }, 3000);
      ambNodesRef.current.giggle = { stop: () => clearInterval(interval) };
    }
  };
  const stopAmb = (key) => {
    if (ambNodesRef.current[key]) { try { ambNodesRef.current[key].stop(); } catch (e) {} delete ambNodesRef.current[key]; }
  };
  const toggleAmb = (key) => {
    setAmbience(prev => {
      const next = { ...prev, [key]: !prev[key] };
      if (next[key]) startAmb(key); else stopAmb(key);
      return next;
    });
  };
  useEffect(() => { startAmb('boing'); return () => Object.keys(ambNodesRef.current).forEach(stopAmb); }, []); // eslint-disable-line

  const handleSfxUpload = (file) => {
    if (!file) return;
    setUploadedSfx(prev => [...prev, { name: file.name, url: URL.createObjectURL(file) }]);
  };

  const wireMusicGraph = () => {
    const ac = ensureAudio();
    if (!musicElRef.current) return;
    if (musicSourceRef.current) return; // createMediaElementSource can only run once per element
    try {
      const src = ac.createMediaElementSource(musicElRef.current);
      const gain = ac.createGain();
      gain.gain.value = musicVolume;
      src.connect(gain);
      gain.connect(mixerGainRef.current);
      musicSourceRef.current = src;
      musicGainRef.current = gain;
    } catch (e) { /* already wired or unsupported */ }
  };

  const handleMusicUpload = (file) => {
    if (!file) return;
    if (!musicElRef.current) {
      musicElRef.current = new Audio();
      musicElRef.current.loop = true;
      musicElRef.current.crossOrigin = 'anonymous';
    }
    const url = URL.createObjectURL(file);
    musicElRef.current.src = url;
    setMusic({ name: file.name, url });
    ensureAudio();
    wireMusicGraph();
  };
  useEffect(() => { if (musicGainRef.current) musicGainRef.current.gain.value = musicVolume; }, [musicVolume]);
  const removeMusic = () => {
    if (musicElRef.current) { musicElRef.current.pause(); }
    setMusic(null);
  };

  // ---------- transport ----------
  const play = () => {
    if (!hasAnyImage) { alert('Pehle kam se kam ek layer image daalo.'); return; }
    Object.values(confettiMapRef.current).forEach(r => { r.current = []; });
    lastClipIndexRef.current = -1;
    setManualTime(0);
    if (music && musicOn && musicElRef.current) {
      musicElRef.current.currentTime = 0;
      musicElRef.current.play().catch(() => {});
    }
    const run = () => start(totalDuration, () => {
      if (loop) run();
      else if (music && musicElRef.current) musicElRef.current.pause();
    });
    run();
  };
  const stopPlayback = () => {
    stop();
    if (musicElRef.current) musicElRef.current.pause();
  };

  const record = async () => {
    if (!hasAnyImage) { alert('Pehle kam se kam ek layer image daalo.'); return; }
    ensureAudio();
    const canvas = canvasRef.current;
    const canvasStream = canvas.captureStream(30);
    const destNode = audioCtxRef.current.createMediaStreamDestination();
    mixerGainRef.current.connect(destNode); // fan-out: speakers keep playing, recorder also gets the same mix
    const combined = new MediaStream([...canvasStream.getVideoTracks(), ...destNode.stream.getAudioTracks()]);
    chunksRef.current = [];
    let mr;
    try {
      mr = new MediaRecorder(combined, { mimeType: 'video/webm;codecs=vp9,opus' });
    } catch (e) {
      mr = new MediaRecorder(combined);
    }
    mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    mr.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      setDownloadUrl(URL.createObjectURL(blob));
      setRecording(false);
      try { mixerGainRef.current.disconnect(destNode); } catch (e) {}
    };
    mediaRecorderRef.current = mr;
    mr.start();
    setRecording(true);
    speak();
    Object.values(confettiMapRef.current).forEach(r => { r.current = []; });
    lastClipIndexRef.current = -1;
    setManualTime(0);
    if (music && musicOn && musicElRef.current) {
      musicElRef.current.currentTime = 0;
      musicElRef.current.play().catch(() => {});
    }
    start(totalDuration, () => {
      if (mr.state !== 'inactive') mr.stop();
      if (musicElRef.current) musicElRef.current.pause();
    });
    setTimeout(() => { if (mr.state !== 'inactive') mr.stop(); }, totalDuration * 1000 + 300);
  };

  const resetScene = () => {
    setClips([makeClip('Scene 1')]);
    setAspectKey('classic'); setLoop(false);
    setMusic(null); setMusicOn(true); setMusicVolume(0.6);
  };
  useEffect(() => { if (!clips.find(c => c.id === selectedClipId)) setSelectedClipId(clips[0].id); }, [clips, selectedClipId]);

  const saveDraft = () => {
    const draft = {
      clips: clips.map(c => ({ ...c, layers: c.layers.map(({ img, ...rest }) => rest) })),
      script, voiceMood, ambience, aspectKey, loop, musicVolume, musicOn,
    };
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      alert('Draft save ho gaya (browser mein). Images aur music dobara upload karni hongi jab load karoge.');
    } catch (e) {
      alert('Draft save nahi ho paya.');
    }
  };
  const loadDraft = () => {
    let raw;
    try { raw = localStorage.getItem(DRAFT_KEY); } catch (e) { raw = null; }
    if (!raw) { alert('Koi saved draft nahi mila.'); return; }
    const d = JSON.parse(raw);
    if (d.clips && d.clips.length) {
      const restored = d.clips.map(c => ({ ...c, layers: c.layers.map(l => ({ ...l, img: null })) }));
      setClips(restored);
      setSelectedClipId(restored[0].id);
    }
    if (d.script != null) setScript(d.script);
    if (d.voiceMood) setVoiceMood(d.voiceMood);
    if (d.aspectKey) setAspectKey(d.aspectKey);
    if (d.loop != null) setLoop(d.loop);
    if (d.musicVolume != null) setMusicVolume(d.musicVolume);
    if (d.musicOn != null) setMusicOn(d.musicOn);
    if (d.ambience) {
      setAmbience(d.ambience);
      Object.keys(ambNodesRef.current).forEach(stopAmb);
      Object.entries(d.ambience).forEach(([k, v]) => { if (v) startAmb(k); });
    }
  };

  // Keyboard shortcut: Space toggles play/stop (ignored while typing in a field).
  const playRef = useRef(play); playRef.current = play;
  const stopRef = useRef(stopPlayback); stopRef.current = stopPlayback;
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || '';
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return;
      if (e.code === 'Space') { e.preventDefault(); playingRef.current ? stopRef.current() : playRef.current(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [playingRef]);

  // Touch-friendly control styles for small screens.
  const T = {
    btn: isMobile ? { ...s.btn, padding: '.65rem 1rem', fontSize: '.85rem' } : s.btn,
    btnPrimary: isMobile ? { ...s.btnPrimary, padding: '.7rem 1.1rem', fontSize: '.85rem', flex: '1 1 auto' } : s.btnPrimary,
    btnDanger: isMobile ? { ...s.btnDanger, padding: '.7rem 1.1rem', fontSize: '.85rem', flex: '1 1 auto' } : s.btnDanger,
    iconBtn: isMobile ? { ...s.iconBtn, padding: '.45rem .65rem', fontSize: '.8rem' } : s.iconBtn,
    select: isMobile ? { ...s.select, fontSize: '16px', padding: '.6rem' } : s.select,
    textarea: isMobile ? { ...s.textarea, fontSize: '16px' } : s.textarea,
  };
  const stageFrameStyle = { ...s.stageFrame, aspectRatio: `${canvasSize.w}/${canvasSize.h}` };
  const playhead = playingRef.current ? elapsed : manualTime;
  const recordProgress = recording ? Math.min(1, elapsed / totalDuration) : 0;

  // Which scene is currently under the playhead (for the "Scene X/Y" readout).
  let activeSceneIdx = 0, cum = 0;
  for (let i = 0; i < clips.length; i++) {
    if (playhead < cum + clips[i].duration || i === clips.length - 1) { activeSceneIdx = i; break; }
    cum += clips[i].duration;
  }

  const fmtTime = (secs) => {
    const m = Math.floor(secs / 60);
    const sec = Math.floor(secs % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div className="animora-shell" style={{ minHeight: '100vh', background: '#f5f4fb' }}>
      <style>{`
        .animora-shell button { font-family: inherit; transition: transform .14s cubic-bezier(.2,.8,.2,1), filter .14s ease, opacity .14s ease; -webkit-tap-highlight-color: transparent; }
        .animora-shell button:not(:disabled) { cursor: pointer; }
        .animora-shell button:not(:disabled):hover { transform: translateY(-1px); filter: brightness(1.06); }
        .animora-shell button:not(:disabled):active { transform: translateY(0) scale(.96); filter: brightness(.95); }
        .animora-shell button:disabled { cursor: not-allowed; }
        .animora-shell button:focus-visible { outline: 2px solid #6c47ff; outline-offset: 2px; }
        .animora-shell input[type="range"] { cursor: pointer; }
        .animora-shell select, .animora-shell textarea, .animora-shell input[type="text"] { transition: border-color .14s ease, box-shadow .14s ease; }
        .animora-shell select:focus, .animora-shell textarea:focus, .animora-shell input[type="text"]:focus { outline: none; border-color: #6c47ff !important; box-shadow: 0 0 0 3px rgba(108,71,255,.14); }
        .animora-shell label:has(input[type="checkbox"]) { transition: transform .14s ease; }
        .animora-shell label:has(input[type="checkbox"]):active { transform: scale(.97); }
      `}</style>
      <Navbar />
      <div style={s.editorPage}>

        {/* ---------- top bar: undo/redo, aspect ratio, export ---------- */}
        <div style={s.topBar}>
          <div style={s.topBarLeft}>
            <span style={s.topBarTitle}>✨ Animora</span>
          </div>
          <div style={s.topBarRight}>
            <button style={{ ...s.topIconBtn, ...(historyRef.current.length ? {} : s.disabled) }} onClick={undo} disabled={!historyRef.current.length} title="Undo">↺</button>
            <button style={{ ...s.topIconBtn, ...(redoRef.current.length ? {} : s.disabled) }} onClick={redo} disabled={!redoRef.current.length} title="Redo">↻</button>
            <select style={s.aspectPill} value={aspectKey} onChange={e => setAspectKey(e.target.value)} title="Aspect ratio">
              {Object.entries(ASPECTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <button style={{ ...s.exportBtn, ...((recording || !hasAnyImage) ? s.disabled : {}) }} onClick={record} disabled={recording || !hasAnyImage}>
              {recording ? '● Exporting…' : 'Export'}
            </button>
          </div>
        </div>
        {downloadUrl && (
          <div style={s.downloadBar}>
            <a style={s.dlLink} href={downloadUrl} download="animora-video.webm">↓ video.webm download karo</a>
          </div>
        )}

        {/* ---------- stage / canvas ---------- */}
        <div style={s.canvasArea}>
          <div style={s.stageCard}>
          <div style={stageFrameStyle}>
            <canvas ref={canvasRef} width={canvasSize.w} height={canvasSize.h} style={s.canvas} />
            {!hasAnyImage && (
              <div style={{ ...s.emptyState, background: selectedClip.bgColor }} onClick={() => setActivePanel('ai')}>
                <span style={s.emptyStateIcon}>🪄</span>
                <span style={s.emptyStateTitle}>Character ya background daalo</span>
                <span>Upload karo ya AI se generate karo</span>
                <span style={s.emptyStateCta}>✨ AI Studio kholo →</span>
              </div>
            )}
            <div style={s.sceneBadge}>Scene {activeSceneIdx + 1}/{clips.length}</div>
            {recording && (
              <div style={s.recBadge}>● REC</div>
            )}
          </div>

          <div style={s.transport}>
            <button style={{ ...s.transportBtn, ...(!hasAnyImage ? s.disabled : {}) }} onClick={play} disabled={!hasAnyImage} title="Preview">▶</button>
            <button style={s.transportBtn} onClick={stopPlayback} title="Stop">■</button>
            <input
              type="range" min="0" max={totalDuration} step="0.05"
              value={playhead}
              onChange={e => { if (playingRef.current) return; setManualTime(+e.target.value); }}
              style={s.scrubRange}
              disabled={playingRef.current || recording}
            />
            <span style={s.scrubTime}>{fmtTime(playhead)} / {fmtTime(totalDuration)}</span>
            <label style={s.loopToggle}>
              <input type="checkbox" checked={loop} onChange={e => setLoop(e.target.checked)} /> loop
            </label>
          </div>
          {recording && (
            <div style={s.progressTrack}>
              <div style={{ ...s.progressFill, width: `${recordProgress * 100}%` }} />
            </div>
          )}
          </div>
        </div>

        {/* ---------- CapCut-style timeline: draggable + trimmable scene clips ---------- */}
        <div style={s.timelineOuter}>
          <div style={s.timelineHeaderRow}>
            <span style={s.timelineHint}>Scene ko drag karke reorder karo · edge kheecho trim karne ke liye</span>
            <div style={s.timelineActions}>
              <button style={T.iconBtn} onClick={() => duplicateClip(selectedClip.id)} title="Scene copy karo">⧉</button>
              <button style={T.iconBtn} onClick={() => removeClip(selectedClip.id)} title="Scene hatao">✕</button>
              {hasLastDeleted && <button style={{ ...T.iconBtn, color: '#6c47ff' }} onClick={undoDeleteClip}>↶ undo delete</button>}
            </div>
          </div>
          <div style={s.timelineScroller} ref={trackRef}>
            <div style={{ ...s.timelinePlayheadLine, left: `${playhead * PX_PER_SEC}px` }} />
            <div style={s.timelineTrackNew}>
              {clips.map((c) => {
                const widthPx = Math.max(70, c.duration * PX_PER_SEC);
                const isSel = c.id === selectedClip.id;
                return (
                  <div
                    key={c.id}
                    style={{ ...s.timelineClipNew, width: widthPx, ...(isSel ? s.timelineClipNewOn : {}), background: `linear-gradient(160deg, ${c.bgColor} 0%, rgba(30,20,60,.55) 130%)` }}
                    onPointerDown={e => beginReorderDrag(e, c.id)}
                    onClick={() => setSelectedClipId(c.id)}
                  >
                    <div style={s.timelineClipStripe} />
                    <span style={s.timelineClipNameNew}>{c.name}</span>
                    <span style={s.timelineClipDurNew}>{c.duration}s</span>
                    <div
                      style={s.trimHandle}
                      onPointerDown={e => beginTrimDrag(e, c.id, c.duration)}
                      title="Duration adjust karne ke liye kheencho"
                    >⋮</div>
                  </div>
                );
              })}
              <button style={s.timelineAddNew} onClick={addClip} title="Naya scene add karo">+ Scene</button>
            </div>
          </div>
        </div>

        <p style={s.note2}>Preview browser mein hi chalta hai. Export pura timeline (sab scenes + music + SFX) ek .webm file mein record karta hai. Space bar se play/stop bhi ho sakta hai.</p>

        {/* ---------- bottom icon toolbar (CapCut-style) ---------- */}
        <div style={s.bottomToolbar}>
          {PANEL_DEFS.map(p => (
            <button
              key={p.key}
              style={{ ...s.toolbarBtn, ...(activePanel === p.key ? s.toolbarBtnActive : {}) }}
              onClick={() => togglePanel(p.key)}
            >
              <span style={s.toolbarIcon}>{p.icon}</span>
              <span style={s.toolbarLabel}>{p.label}</span>
            </button>
          ))}
        </div>

        {/* ---------- slide-up panel: shows the Section content for the active tool ---------- */}
        {activePanel && (
          <>
            <div style={s.panelBackdrop} onClick={() => setActivePanel(null)} />
            <div style={s.panelDrawer}>
              <div style={s.panelHeader}>
                <span style={s.panelTitle}>{PANEL_DEFS.find(p => p.key === activePanel)?.label}</span>
                <button style={s.panelClose} onClick={() => setActivePanel(null)}>✕</button>
              </div>
              <div style={s.panelBody}>

                {activePanel === 'canvas' && (
                  <>
                    <Section title="Canvas">
                      <div style={s.row}>
                        <Field label="Aspect ratio">
                          <select style={T.select} value={aspectKey} onChange={e => setAspectKey(e.target.value)}>
                            {Object.entries(ASPECTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                          </select>
                        </Field>
                      </div>
                    </Section>
                    <Section title={`Scene: "${selectedClip.name}"`}>
                      <Field label="Scene naam">
                        <input style={T.select} value={selectedClip.name} onChange={e => renameClip(selectedClip.id, e.target.value)} />
                      </Field>
                      <div style={s.row}>
                        <Field label="Background color">
                          <input type="color" value={selectedClip.bgColor} onChange={e => patchClip(selectedClip.id, { bgColor: e.target.value })} style={s.colorInput} />
                        </Field>
                      </div>
                      <div style={s.swatchRow}>
                        {BG_PRESETS.map(c => (
                          <button key={c} onClick={() => patchClip(selectedClip.id, { bgColor: c })} title={c} style={{ ...s.swatch, background: c, ...(selectedClip.bgColor === c ? s.swatchOn : {}) }} />
                        ))}
                      </div>
                    </Section>
                  </>
                )}

                {activePanel === 'edit' && (
                  <Section title="Layers">
                    {selectedClip.layers.map((layer, idx) => (
                      <div key={layer.id} style={s.layerRow}>
                        <div style={s.layerRowTop}>
                          <span style={s.layerTag}>{layer.name}</span>
                          <input type="file" accept="image/*" onChange={e => setLayerImage(selectedClip.id, layer.id, e.target.files[0])} style={s.fileInput} />
                          <button style={T.iconBtn} onClick={() => removeLayer(selectedClip.id, layer.id)} title="Layer hatao">✕</button>
                        </div>
                        <div style={s.layerRowBottom}>
                          <button style={T.iconBtn} onClick={() => moveLayer(selectedClip.id, layer.id, -1)} disabled={idx === 0} title="Upar le jao">↑</button>
                          <button style={T.iconBtn} onClick={() => moveLayer(selectedClip.id, layer.id, 1)} disabled={idx === selectedClip.layers.length - 1} title="Niche le jao">↓</button>
                          <button style={T.iconBtn} onClick={() => duplicateLayer(selectedClip.id, layer.id)} title="Copy banao">⧉</button>
                          <button style={{ ...T.iconBtn, ...(layer.visible === false ? s.iconBtnOff : {}) }} onClick={() => toggleLayerVisible(selectedClip.id, layer.id)} title="Show/hide">
                            {layer.visible === false ? '🚫' : '👁'}
                          </button>
                          <label style={s.miniLabel} title="Parallax depth">
                            depth
                            <input type="range" min="0" max="1" step="0.05" value={layer.depth} onChange={e => setLayerDepth(selectedClip.id, layer.id, +e.target.value)} style={s.rangeSmall} />
                          </label>
                          <label style={s.miniLabel} title="Layer opacity">
                            opacity
                            <input type="range" min="0" max="1" step="0.05" value={layer.opacity == null ? 1 : layer.opacity} onChange={e => setLayerOpacity(selectedClip.id, layer.id, +e.target.value)} style={s.rangeSmall} />
                          </label>
                        </div>
                      </div>
                    ))}
                    <button style={T.btn} onClick={() => addLayer(selectedClip.id)}>+ layer add karo</button>
                  </Section>
                )}

                {activePanel === 'ai' && (
                  <Section title="AI Studio">
                    <div style={s.tabRow}>
                      {[['image', '🖼️ Image'], ['voice', '🎙️ Voice'], ['lipsync', '👄 Lip-sync']].map(([key, label]) => (
                        <button key={key} style={{ ...s.tabBtn, ...(aiSubTab === key ? s.tabBtnOn : {}) }} onClick={() => setAiSubTab(key)}>{label}</button>
                      ))}
                    </div>

                    {aiSubTab === 'image' && (
                      <div>
                        <p style={s.note}>Prompt se character ya background banao — {AI_IMAGE_STYLE_TOTAL_COUNT}+ styles available.</p>
                        <textarea style={{ ...T.textarea, minHeight: '54px' }} value={aiPrompt} onChange={e => setAiPrompt(e.target.value)} placeholder="e.g. neon-colored velociraptor DJ with headphones" />
                        <Field label="Style category">
                          <select style={T.select} value={aiCategory} onChange={e => { setAiCategory(e.target.value); setAiStyle(AI_IMAGE_STYLE_CATEGORIES.find(c => c.category === e.target.value).styles[0]); }}>
                            {AI_IMAGE_STYLE_CATEGORIES.map(c => <option key={c.category} value={c.category}>{c.category}</option>)}
                          </select>
                        </Field>
                        <div style={s.styleGrid}>
                          {AI_IMAGE_STYLE_CATEGORIES.find(c => c.category === aiCategory).styles.map(st => (
                            <button key={st} style={{ ...s.styleChip, ...(aiStyle === st ? s.styleChipOn : {}) }} onClick={() => setAiStyle(st)}>{st}</button>
                          ))}
                        </div>
                        <Field label="Apply result to">
                          <select style={T.select} value={aiImageTarget} onChange={e => setAiImageTarget(e.target.value)}>
                            {selectedClip.layers.map(l => <option key={l.id} value={l.name}>{l.name} layer</option>)}
                            <option value="new">+ new layer</option>
                          </select>
                        </Field>
                        <button style={{ ...T.btnPrimary, width: '100%' }} onClick={generateAIImages} disabled={aiGeneratingImage || !aiPrompt.trim()}>
                          {aiGeneratingImage ? '✨ Generate ho raha hai…' : `✨ Generate (${aiStyle})`}
                        </button>
                        {aiImageResults.length > 0 && (
                          <div style={s.aiResultGrid}>
                            {aiImageResults.map(r => (
                              <button key={r.id} style={s.aiResultThumb} onClick={() => applyAIImage(r)} title="Layer par apply karo">
                                <img src={r.url} alt={r.style} style={s.aiResultImg} />
                                <span style={s.aiResultUse}>Use ✓</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {aiSubTab === 'voice' && (
                      <div>
                        <p style={s.note}>Script se AI narration takes banao (persona select karo, phir generate).</p>
                        <textarea style={T.textarea} value={script} onChange={e => setScript(e.target.value)} placeholder="Narration script yahan likho..." />
                        <div style={s.personaGrid}>
                          {AI_VOICE_PERSONAS.map(p => (
                            <button key={p.id} style={{ ...s.personaChip, ...(aiPersonaId === p.id ? s.personaChipOn : {}) }} onClick={() => setAiPersonaId(p.id)}>
                              <span style={{ fontWeight: 800 }}>{p.name}</span>
                              <span style={{ fontSize: '.62rem', opacity: 0.75 }}>{p.tag}</span>
                            </button>
                          ))}
                        </div>
                        <button style={{ ...T.btnPrimary, width: '100%', marginTop: '.6rem' }} onClick={generateAIVoiceTakes} disabled={aiGeneratingVoice || !script.trim()}>
                          {aiGeneratingVoice ? '🎙️ Generate ho raha hai…' : '🎙️ Generate voice takes'}
                        </button>
                        {aiVoiceTakes.map(take => (
                          <div key={take.id} style={s.sfxItem}>
                            <span>{take.label} · {take.durationSec}s</span>
                            <div style={{ display: 'flex', gap: '.4rem' }}>
                              <button style={T.iconBtn} onClick={take.play} title="Play">▶</button>
                              <button style={{ ...T.iconBtn, ...(selectedTakeId === take.id ? s.iconBtnUse : {}) }} onClick={() => useVoiceTake(take)}>
                                {selectedTakeId === take.id ? 'In use ✓' : 'Use'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {aiSubTab === 'lipsync' && (
                      <div>
                        <label style={{ ...s.toggle, ...(selectedClip.lipSync.enabled ? s.toggleOn : {}), width: '100%', marginBottom: '.8rem' }}>
                          <input type="checkbox" checked={selectedClip.lipSync.enabled} onChange={() => toggleLipSync(selectedClip.id)} /> Is scene ke liye auto lip-sync on karo
                        </label>
                        <Field label={`Intensity: ${selectedClip.lipSync.intensity}%`}>
                          <input type="range" min="10" max="100" value={selectedClip.lipSync.intensity} onChange={e => setLipSyncIntensity(selectedClip.id, +e.target.value)} style={s.range} disabled={!selectedClip.lipSync.enabled} />
                        </Field>
                        <Field label="Audio source">
                          <select style={T.select} value={selectedClip.lipSync.source} onChange={e => setLipSyncSource(selectedClip.id, e.target.value)} disabled={!selectedClip.lipSync.enabled}>
                            <option value="voiceover">Voice-over narration</option>
                            <option value="ai-voice">AI generated voice take</option>
                          </select>
                        </Field>
                        <p style={s.note}>Character layer ke mooh par ek chhota sync indicator dikhega — abhi ye preview stub hai, asli viseme-based mouth-shapes baad me AI API ke saath aayenge.</p>
                      </div>
                    )}
                  </Section>
                )}

                {activePanel === 'camera' && (
                  <Section title="Camera movement">
                    <Field label="Movement type">
                      <select style={T.select} value={selectedClip.camType} onChange={e => patchClip(selectedClip.id, { camType: e.target.value })}>
                        {CAM_TYPES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </Field>
                    <div style={s.row}>
                      <Field label={`Intensity: ${selectedClip.camIntensity}%`}>
                        <input type="range" min="5" max="60" value={selectedClip.camIntensity} onChange={e => patchClip(selectedClip.id, { camIntensity: +e.target.value })} style={s.range} />
                      </Field>
                      <Field label={`Duration: ${selectedClip.duration}s`}>
                        <input type="range" min="2" max="20" value={selectedClip.duration} onChange={e => patchClip(selectedClip.id, { duration: +e.target.value })} style={s.range} />
                      </Field>
                    </div>
                  </Section>
                )}

                {activePanel === 'filters' && (
                  <Section title="Filter & speed">
                    <div style={s.row}>
                      <Field label="Color filter">
                        <select style={T.select} value={selectedClip.filter} onChange={e => patchClip(selectedClip.id, { filter: e.target.value })}>
                          {Object.entries(FILTERS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                      </Field>
                      <Field label={`Speed: ${selectedClip.speed.toFixed(1)}x`}>
                        <input type="range" min="0.5" max="2" step="0.1" value={selectedClip.speed} onChange={e => patchClip(selectedClip.id, { speed: +e.target.value })} style={s.range} />
                      </Field>
                    </div>
                  </Section>
                )}

                {activePanel === 'transition' && (
                  <Section title="Transition-in">
                    {selectedIdx === 0 ? (
                      <p style={s.note}>Pehle scene se pehle koi transition nahi hota.</p>
                    ) : (
                      <Field label="Transition type">
                        <select style={T.select} value={selectedClip.transition} onChange={e => patchClip(selectedClip.id, { transition: e.target.value })}>
                          {Object.entries(TRANSITIONS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                        </select>
                      </Field>
                    )}
                  </Section>
                )}

                {activePanel === 'text' && (
                  <Section title="Text / captions">
                    {selectedClip.texts.map(txt => (
                      <div key={txt.id} style={s.layerRow}>
                        <div style={s.layerRowTop}>
                          <input style={{ ...T.select, flex: 1 }} value={txt.text} onChange={e => updateText(selectedClip.id, txt.id, { text: e.target.value })} placeholder="Caption text" />
                          <button style={T.iconBtn} onClick={() => removeText(selectedClip.id, txt.id)} title="Text hatao">✕</button>
                        </div>
                        <div style={s.layerRowBottom}>
                          <select style={{ ...s.select, width: 'auto' }} onChange={e => setTextPosition(selectedClip.id, txt.id, e.target.value)} defaultValue="bottom">
                            {Object.entries(TEXT_POSITIONS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                          </select>
                          <select style={{ ...s.select, width: 'auto' }} value={txt.anim} onChange={e => updateText(selectedClip.id, txt.id, { anim: e.target.value })}>
                            {Object.entries(TEXT_ANIMS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                          </select>
                          <input type="color" value={txt.color} onChange={e => updateText(selectedClip.id, txt.id, { color: e.target.value })} style={{ width: '32px', height: '32px', borderRadius: '6px', border: 'none', cursor: 'pointer' }} />
                          <label style={s.miniLabel} title="Font size">
                            size
                            <input type="range" min="18" max="80" value={txt.size} onChange={e => updateText(selectedClip.id, txt.id, { size: +e.target.value })} style={s.rangeSmall} />
                          </label>
                          <label style={s.miniLabel} title="Kab dikhna shuru ho (scene ke andar)">
                            delay
                            <input type="range" min="0" max="0.8" step="0.05" value={txt.delay} onChange={e => updateText(selectedClip.id, txt.id, { delay: +e.target.value })} style={s.rangeSmall} />
                          </label>
                        </div>
                      </div>
                    ))}
                    <button style={T.btn} onClick={() => addText(selectedClip.id)}>+ text add karo</button>
                  </Section>
                )}

                {activePanel === 'effects' && (
                  <Section title="Comic / toon VFX">
                    <div style={s.toggleGrid}>
                      {[
                        ['outline', 'Cel outline'],
                        ['colorPop', 'Bright color pop'],
                        ['halftone', 'Halftone dots'],
                        ['speedLines', 'Speed lines'],
                        ['sparkle', 'Sparkle twinkle'],
                        ['confetti', 'Confetti burst'],
                        ['popFlash', '"POP!" flash'],
                      ].map(([key, label]) => (
                        <label key={key} style={{ ...s.toggle, ...(selectedClip.vfx[key] ? s.toggleOn : {}) }}>
                          <input type="checkbox" checked={selectedClip.vfx[key]} onChange={() => toggleVfx(selectedClip.id, key)} /> {label}
                        </label>
                      ))}
                    </div>
                  </Section>
                )}

                {activePanel === 'voice' && (
                  <Section title="Voice-over">
                    <textarea style={T.textarea} value={script} onChange={e => setScript(e.target.value)} placeholder="Narration yahan likho..." />
                    <div style={s.row}>
                      <Field label="Voice">
                        <select style={T.select} value={voiceIdx} onChange={e => setVoiceIdx(+e.target.value)}>
                          {voices.map((v, i) => <option key={i} value={i}>{v.name} ({v.lang})</option>)}
                        </select>
                      </Field>
                      <Field label="Mood">
                        <select style={T.select} value={voiceMood} onChange={e => setVoiceMood(e.target.value)}>
                          <option value="cheerful">Cheerful & bouncy</option>
                          <option value="silly">Silly & squeaky</option>
                          <option value="normal">Normal</option>
                        </select>
                      </Field>
                    </div>
                    <button style={T.btn} onClick={speak}>🔊 test narration</button>
                  </Section>
                )}

                {activePanel === 'music' && (
                  <Section title="Music & SFX">
                    <Field label="Background music upload karo">
                      <input type="file" accept="audio/*" onChange={e => handleMusicUpload(e.target.files[0])} />
                    </Field>
                    {music && (
                      <div style={s.sfxItem}>
                        <span>{music.name}</span>
                        <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
                          <label style={s.miniLabel}>vol <input type="range" min="0" max="1" step="0.05" value={musicVolume} onChange={e => setMusicVolume(+e.target.value)} style={s.rangeSmall} /></label>
                          <button style={T.iconBtn} onClick={() => setMusicOn(v => !v)}>{musicOn ? 'on' : 'off'}</button>
                          <button style={T.iconBtn} onClick={removeMusic} title="Music hatao">✕</button>
                        </div>
                      </div>
                    )}
                    <div style={{ ...s.toggleGrid, marginTop: '.8rem' }}>
                      {[['boing', 'Boing bounce'], ['chime', 'Happy chime'], ['giggle', 'Giggle blip']].map(([key, label]) => (
                        <label key={key} style={{ ...s.toggle, ...(ambience[key] ? s.toggleOn : {}) }}>
                          <input type="checkbox" checked={ambience[key]} onChange={() => toggleAmb(key)} /> {label}
                        </label>
                      ))}
                    </div>
                    <Field label="Apni SFX file add karo (optional)">
                      <input type="file" accept="audio/*" onChange={e => handleSfxUpload(e.target.files[0])} />
                    </Field>
                    {uploadedSfx.map((sfx, i) => (
                      <div key={i} style={s.sfxItem}>
                        <span>{sfx.name}</span>
                        <button style={T.iconBtn} onClick={() => new Audio(sfx.url).play()}>▶</button>
                      </div>
                    ))}
                  </Section>
                )}

                {activePanel === 'project' && (
                  <Section title="Project">
                    <div style={s.row}>
                      <button style={T.btn} onClick={saveDraft}>💾 draft save karo</button>
                      <button style={T.btn} onClick={loadDraft}>📂 draft load karo</button>
                    </div>
                    <button style={{ ...T.btn, marginTop: '.6rem' }} onClick={resetScene}>↺ pura project reset karo</button>
                    <p style={s.note}>Draft browser ki local storage mein save hota hai (sirf settings — images aur music alag se dobara upload karni hongi).</p>
                  </Section>
                )}

              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={s.section}>
      <h2 style={s.sectionTitle}>{title}</h2>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div style={s.field}>
      <label style={s.label}>{label}</label>
      {children}
    </div>
  );
}

// ---------- design tokens (AutoDraft-style: light, card-based, bright accents) ----------
const COLORS = {
  bg: '#f5f4fb',
  card: '#ffffff',
  border: '#ecebf5',
  borderStrong: '#dcdaf0',
  text: '#1a1a2e',
  textSoft: '#6b6b85',
  textFaint: '#9a99b3',
  accent1: '#6c47ff',
  accent2: '#ff6b93',
  accentGrad: 'linear-gradient(135deg,#6c47ff,#ff6b93)',
  accentGradSoft: 'linear-gradient(135deg,rgba(108,71,255,.10),rgba(255,107,147,.10))',
  chipBg: '#f7f6fd',
  shadowCard: '0 2px 14px rgba(30,20,80,.07)',
  shadowFloat: '0 -6px 24px rgba(30,20,80,.10)',
  danger: '#ff6b93',
  success: '#1fb17a',
};

const s = {
  // ---------- editor shell ----------
  editorPage: { maxWidth: '640px', margin: '0 auto', padding: '64px 0 84px', display: 'flex', flexDirection: 'column' },

  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.7rem .9rem', background: COLORS.card, borderBottom: `1px solid ${COLORS.border}`, boxShadow: COLORS.shadowCard, position: 'relative', zIndex: 2 },
  topBarLeft: { display: 'flex', alignItems: 'center', gap: '.5rem' },
  topBarTitle: { fontSize: '.9rem', fontWeight: 800, background: COLORS.accentGrad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' },
  topBarRight: { display: 'flex', alignItems: 'center', gap: '.4rem' },
  topIconBtn: { background: COLORS.chipBg, border: `1px solid ${COLORS.border}`, color: COLORS.text, fontSize: '1rem', width: '32px', height: '32px', borderRadius: '10px', cursor: 'pointer' },
  aspectPill: { background: COLORS.chipBg, border: `1px solid ${COLORS.border}`, color: COLORS.text, borderRadius: '999px', padding: '.35rem .7rem', fontSize: '.72rem', cursor: 'pointer', fontWeight: 600 },
  exportBtn: { fontFamily: 'inherit', background: COLORS.accentGrad, border: 'none', color: '#fff', padding: '.55rem 1.3rem', borderRadius: '999px', fontSize: '.8rem', fontWeight: 800, letterSpacing: '.01em', cursor: 'pointer', boxShadow: '0 4px 14px rgba(108,71,255,.30)' },
  downloadBar: { textAlign: 'center', padding: '.5rem', background: COLORS.accentGradSoft },

  canvasArea: { display: 'flex', flexDirection: 'column', gap: '.7rem', padding: '1rem .8rem 0' },
  stageCard: { background: COLORS.card, borderRadius: '20px', padding: '.9rem', boxShadow: COLORS.shadowCard, border: `1px solid ${COLORS.border}` },
  stageFrame: { position: 'relative', width: '100%', maxWidth: '360px', margin: '0 auto', aspectRatio: '18/25', background: '#0b0b16', border: `1px solid ${COLORS.border}`, borderRadius: '14px', overflow: 'hidden' },
  canvas: { width: '100%', height: '100%', display: 'block' },
  emptyState: { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: '#c8c6e8', fontSize: '.8rem', padding: '2rem', cursor: 'pointer', gap: '.5rem' },
  emptyStateIcon: { fontSize: '2.2rem' },
  emptyStateTitle: { fontSize: '.85rem', fontWeight: 800, color: '#f0f0f8' },
  emptyStateCta: { marginTop: '.3rem', fontSize: '.72rem', fontWeight: 700, background: COLORS.accentGrad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' },
  sceneBadge: { position: 'absolute', top: '.6rem', left: '.6rem', background: 'rgba(0,0,0,.5)', color: '#fff', fontSize: '.68rem', fontWeight: 700, padding: '.25rem .6rem', borderRadius: '999px', pointerEvents: 'none' },
  recBadge: { position: 'absolute', top: '.6rem', right: '.6rem', background: COLORS.accent2, color: '#fff', fontSize: '.62rem', fontWeight: 800, padding: '.2rem .5rem', borderRadius: '999px', pointerEvents: 'none' },

  transport: { display: 'flex', alignItems: 'center', gap: '.6rem', maxWidth: '360px', margin: '.7rem auto 0', width: '100%' },
  transportBtn: { background: COLORS.chipBg, border: `1px solid ${COLORS.border}`, color: COLORS.text, width: '36px', height: '36px', borderRadius: '999px', fontSize: '.85rem', cursor: 'pointer', flex: '0 0 auto' },
  scrubRange: { flex: 1, minWidth: 0, accentColor: COLORS.accent1 },
  scrubTime: { fontSize: '.65rem', color: COLORS.textSoft, minWidth: '68px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', flex: '0 0 auto' },
  loopToggle: { display: 'inline-flex', alignItems: 'center', gap: '.3rem', fontSize: '.68rem', color: COLORS.textSoft, cursor: 'pointer', flex: '0 0 auto' },
  progressTrack: { width: '100%', maxWidth: '360px', margin: '.5rem auto 0', height: '5px', background: COLORS.border, borderRadius: '999px', overflow: 'hidden' },
  progressFill: { height: '100%', background: COLORS.accentGrad, transition: 'width .1s linear' },

  disabled: { opacity: 0.4, cursor: 'not-allowed' },
  dlLink: { color: COLORS.accent1, fontSize: '.78rem', textDecoration: 'none', fontWeight: 700, borderBottom: `1px dotted ${COLORS.accent1}` },
  note: { fontSize: '.72rem', color: COLORS.textSoft, lineHeight: 1.6, maxWidth: '48ch' },
  note2: { fontSize: '.68rem', color: COLORS.textFaint, lineHeight: 1.6, padding: '.4rem .8rem 0', textAlign: 'center' },
  btn: { fontFamily: 'inherit', background: COLORS.chipBg, border: `1px solid ${COLORS.border}`, color: COLORS.text, padding: '.6rem 1.05rem', borderRadius: '10px', fontSize: '.8rem', fontWeight: 700, letterSpacing: '.005em', cursor: 'pointer' },
  btnPrimary: { fontFamily: 'inherit', background: COLORS.accentGrad, border: 'none', color: '#fff', padding: '.6rem 1.05rem', borderRadius: '10px', fontSize: '.8rem', fontWeight: 800, letterSpacing: '.005em', cursor: 'pointer', boxShadow: '0 4px 14px rgba(108,71,255,.28)' },
  btnDanger: { fontFamily: 'inherit', background: '#fff0f4', border: `1px solid rgba(255,107,147,.35)`, color: COLORS.danger, padding: '.6rem 1.05rem', borderRadius: '10px', fontSize: '.8rem', fontWeight: 700, letterSpacing: '.005em', cursor: 'pointer' },

  // ---------- draggable / trimmable timeline ----------
  timelineOuter: { display: 'flex', flexDirection: 'column', gap: '.5rem', padding: '1rem .8rem 0' },
  timelineHeaderRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.5rem', flexWrap: 'wrap' },
  timelineHint: { fontSize: '.62rem', color: COLORS.textFaint },
  timelineActions: { display: 'flex', gap: '.35rem', flexWrap: 'wrap' },
  timelineScroller: { position: 'relative', overflowX: 'auto', background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: '14px', padding: '.7rem', boxShadow: COLORS.shadowCard },
  timelineTrackNew: { display: 'flex', gap: `${CLIP_GAP}px`, position: 'relative' },
  timelinePlayheadLine: { position: 'absolute', top: 0, bottom: 0, width: '2px', background: COLORS.accent2, zIndex: 5, pointerEvents: 'none' },
  timelineClipNew: { position: 'relative', flex: '0 0 auto', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '.1rem', border: `1px solid ${COLORS.border}`, borderRadius: '10px', padding: '.45rem .6rem', cursor: 'grab', overflow: 'hidden', touchAction: 'none', userSelect: 'none' },
  timelineClipNewOn: { border: `1.5px solid ${COLORS.accent1}`, boxShadow: '0 0 0 3px rgba(108,71,255,.14)' },
  timelineClipStripe: { position: 'absolute', inset: 0, background: 'repeating-linear-gradient(90deg, rgba(0,0,0,.03) 0 8px, transparent 8px 16px)', pointerEvents: 'none' },
  timelineClipNameNew: { fontSize: '.68rem', color: '#fff', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', position: 'relative', textShadow: '0 1px 3px rgba(0,0,0,.35)' },
  timelineClipDurNew: { fontSize: '.6rem', color: 'rgba(255,255,255,.85)', position: 'relative', textShadow: '0 1px 3px rgba(0,0,0,.35)' },
  trimHandle: { position: 'absolute', top: 0, bottom: 0, right: 0, width: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(108,71,255,.55)', color: '#fff', fontSize: '.7rem', cursor: 'ew-resize', touchAction: 'none' },
  timelineAddNew: { flex: '0 0 auto', background: 'transparent', border: `1px dashed ${COLORS.borderStrong}`, color: COLORS.accent1, borderRadius: '10px', padding: '.4rem .8rem', fontSize: '.72rem', fontWeight: 700, cursor: 'pointer', alignSelf: 'stretch' },

  // ---------- bottom icon toolbar ----------
  bottomToolbar: { display: 'flex', overflowX: 'auto', gap: '.25rem', padding: '.7rem .5rem', marginTop: '.7rem', borderTop: `1px solid ${COLORS.border}`, background: COLORS.card, boxShadow: '0 -2px 14px rgba(30,20,80,.05)' },
  toolbarBtn: { flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '.2rem', background: 'transparent', border: 'none', color: COLORS.textSoft, padding: '.35rem .55rem', borderRadius: '12px', cursor: 'pointer', minWidth: '58px' },
  toolbarBtnActive: { color: COLORS.accent1, background: COLORS.accentGradSoft },
  toolbarIcon: { fontSize: '1.2rem' },
  toolbarLabel: { fontSize: '.6rem', fontWeight: 700 },

  // ---------- slide-up panel (bottom sheet) ----------
  panelBackdrop: { position: 'fixed', inset: 0, background: 'rgba(20,15,40,.35)', zIndex: 40 },
  panelDrawer: { position: 'fixed', left: 0, right: 0, bottom: 0, maxHeight: '72vh', background: COLORS.card, borderTop: `1px solid ${COLORS.border}`, borderRadius: '22px 22px 0 0', zIndex: 41, display: 'flex', flexDirection: 'column', boxShadow: COLORS.shadowFloat },
  panelHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.9rem 1.1rem', borderBottom: `1px solid ${COLORS.border}` },
  panelTitle: { fontSize: '.88rem', fontWeight: 800, color: COLORS.text },
  panelClose: { background: COLORS.chipBg, border: 'none', color: COLORS.textSoft, fontSize: '1rem', cursor: 'pointer', width: '30px', height: '30px', borderRadius: '999px' },
  panelBody: { padding: '1.1rem', overflowY: 'auto' },

  // ---------- AI Studio panel ----------
  tabRow: { display: 'flex', gap: '.4rem', marginBottom: '1rem', background: COLORS.chipBg, padding: '.25rem', borderRadius: '12px' },
  tabBtn: { flex: 1, fontFamily: 'inherit', background: 'transparent', border: 'none', color: COLORS.textSoft, padding: '.5rem .4rem', borderRadius: '9px', fontSize: '.72rem', fontWeight: 700, cursor: 'pointer' },
  tabBtnOn: { background: COLORS.card, color: COLORS.accent1, boxShadow: '0 2px 8px rgba(30,20,80,.10)' },
  styleGrid: { display: 'flex', flexWrap: 'wrap', gap: '.4rem', marginBottom: '.9rem' },
  styleChip: { fontFamily: 'inherit', background: COLORS.chipBg, border: `1px solid ${COLORS.border}`, color: COLORS.textSoft, padding: '.4rem .8rem', borderRadius: '999px', fontSize: '.7rem', fontWeight: 600, cursor: 'pointer' },
  styleChipOn: { background: COLORS.accentGrad, borderColor: 'transparent', color: '#fff', fontWeight: 800, boxShadow: '0 3px 10px rgba(108,71,255,.30)' },
  aiResultGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.7rem', marginTop: '1rem' },
  aiResultThumb: { position: 'relative', padding: 0, border: `1px solid ${COLORS.border}`, borderRadius: '14px', overflow: 'hidden', cursor: 'pointer', background: COLORS.chipBg, boxShadow: COLORS.shadowCard },
  aiResultImg: { width: '100%', display: 'block', aspectRatio: '1/1', objectFit: 'cover' },
  aiResultUse: { position: 'absolute', bottom: '.4rem', right: '.4rem', background: COLORS.accentGrad, color: '#fff', fontSize: '.62rem', fontWeight: 800, padding: '.2rem .6rem', borderRadius: '999px', boxShadow: '0 2px 8px rgba(0,0,0,.25)' },
  personaGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.55rem', marginTop: '.8rem' },
  personaChip: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '.15rem', fontFamily: 'inherit', background: COLORS.chipBg, border: `1px solid ${COLORS.border}`, color: COLORS.textSoft, padding: '.6rem .7rem', borderRadius: '12px', cursor: 'pointer', textAlign: 'left' },
  personaChipOn: { borderColor: COLORS.accent1, background: COLORS.accentGradSoft, color: COLORS.accent1, boxShadow: '0 2px 8px rgba(108,71,255,.14)' },
  iconBtnUse: { borderColor: COLORS.success, color: COLORS.success, background: '#effaf4' },

  desk: {},
  section: { borderBottom: `1px solid ${COLORS.border}`, paddingBottom: '1.3rem', marginBottom: '1.3rem' },
  sectionTitle: { fontSize: '.9rem', fontWeight: 800, color: COLORS.text, margin: '0 0 .9rem 0' },

  layerRow: { display: 'flex', flexDirection: 'column', gap: '.5rem', marginBottom: '.6rem', padding: '.6rem', background: COLORS.chipBg, border: `1px solid ${COLORS.border}`, borderRadius: '12px' },
  layerRowTop: { display: 'flex', gap: '.5rem', alignItems: 'center', flexWrap: 'wrap' },
  layerRowBottom: { display: 'flex', gap: '.5rem', alignItems: 'center', flexWrap: 'wrap' },
  layerTag: { fontSize: '.68rem', color: COLORS.accent1, fontWeight: 700, minWidth: '62px' },
  fileInput: { flex: 1, minWidth: '110px', fontSize: '.68rem', color: COLORS.textSoft },
  rangeSmall: { width: '64px', verticalAlign: 'middle', marginLeft: '.3rem', accentColor: COLORS.accent1 },
  miniLabel: { display: 'inline-flex', alignItems: 'center', fontSize: '.65rem', color: COLORS.textSoft },
  iconBtn: { background: COLORS.card, border: `1px solid ${COLORS.border}`, color: COLORS.text, borderRadius: '8px', padding: '.3rem .55rem', fontSize: '.7rem', cursor: 'pointer' },
  iconBtnOff: { borderColor: 'rgba(255,107,147,.4)', color: COLORS.danger, background: '#fff0f4' },

  field: { marginBottom: '.9rem', flex: 1 },
  label: { display: 'block', fontSize: '.72rem', color: COLORS.textSoft, fontWeight: 600, marginBottom: '.35rem' },
  select: { width: '100%', background: COLORS.chipBg, border: `1px solid ${COLORS.border}`, color: COLORS.text, borderRadius: '10px', padding: '.55rem', fontSize: '.8rem' },
  colorInput: { width: '100%', height: '38px', background: COLORS.chipBg, border: `1px solid ${COLORS.border}`, borderRadius: '10px', cursor: 'pointer' },
  swatchRow: { display: 'flex', gap: '.5rem', marginTop: '.2rem' },
  swatch: { width: '28px', height: '28px', borderRadius: '999px', border: `2px solid ${COLORS.border}`, cursor: 'pointer', padding: 0 },
  swatchOn: { border: `2px solid ${COLORS.accent1}` },
  textarea: { width: '100%', minHeight: '70px', background: COLORS.chipBg, border: `1px solid ${COLORS.border}`, color: COLORS.text, borderRadius: '10px', padding: '.65rem', fontSize: '.8rem', fontFamily: 'inherit', resize: 'vertical', marginBottom: '.9rem' },
  row: { display: 'flex', gap: '1rem', flexWrap: 'wrap' },
  range: { width: '100%', accentColor: COLORS.accent1 },

  toggleGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.5rem' },
  toggle: { display: 'flex', alignItems: 'center', gap: '.5rem', fontSize: '.76rem', padding: '.55rem', border: `1px solid ${COLORS.border}`, borderRadius: '10px', background: COLORS.chipBg, color: COLORS.textSoft, cursor: 'pointer', fontWeight: 600 },
  toggleOn: { borderColor: COLORS.accent1, background: COLORS.accentGradSoft, color: COLORS.accent1 },

  sfxItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '.72rem', background: COLORS.chipBg, border: `1px solid ${COLORS.border}`, borderRadius: '10px', padding: '.5rem .7rem', marginTop: '.4rem' },
};
